'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { imageSource } from '~/helpers/ImageHelper';
interface Logo {
    name: string;
    id: number;
    img: React.ComponentType<React.PropsWithChildren<React.PropsWithChildren<{ className?: string }>>>;
}

const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

const logoIds = [
    137, 164, 172, 174, 185, 188, 193, 205, 233, 235, 267, 269, 277, 282, 308, 341, 352, 361, 362, 404, 408, 411, 421,
    426, 440, 441, 442, 465, 491, 493, 498, 536, 539, 543, 545, 548, 549, 557, 561, 578, 593, 594, 601, 611, 612, 614,
    615, 616, 620, 653, 654, 659, 666, 672, 676, 679, 701, 704, 747, 749, 752, 785, 787, 789, 818, 834, 850, 866, 871,
    873, 881, 892, 894, 1008, 1028, 1036, 1041, 1052, 1094, 1098, 1107, 1113, 1114, 1118, 1123, 1125, 1132, 1137, 1138,
    1139, 1140, 1142, 1159, 1163, 1164, 1168, 1170, 1174, 1178, 1200, 1205, 1206, 1207, 1216, 1271, 1348, 1354, 1363,
    1370, 1411, 1416, 1424, 1442, 1444, 1450, 1454, 1461, 1467, 1478, 1496, 1497, 1500, 1509, 1518, 1519, 1521, 1523,
    1525, 1535, 1537, 1538, 1540, 1546, 1552, 1556, 1582, 1584, 1593, 1602, 1605, 1612, 1613, 1615, 1626, 1627, 1633,
    1644, 1682, 1687, 1690, 1692, 1733, 1739, 1747, 1761, 1763, 1770, 1776, 1781, 1784, 1788, 1793, 1810, 1814, 1820,
    1829, 1830, 1837, 1850, 1854, 1855, 1864, 1867, 1890, 1895, 1904, 1911, 1914, 1945, 1946, 1947, 1950, 1959, 1960,
    1963, 1968, 1979, 1984, 1985, 1988, 1996, 2000, 2003, 2005, 2013, 2020, 2021, 2024, 2025, 2052, 2058, 2075, 2111,
    2117, 2133, 2141, 2144, 2150, 2153, 2169, 2179, 2190, 2213, 2216, 2223, 2251, 2257, 2261, 2266, 2270, 2271, 2283,
    2287, 2291, 2292, 2296, 2298, 2299, 2310, 2313, 2316, 2319, 2322, 2343, 2345, 2356, 2360, 2365, 2381, 2385, 2388,
    2397, 2404, 2411, 2412, 2416, 2419, 2422, 2431, 2443, 2446, 2449, 2464, 2467, 2468, 2470, 2497, 2504, 2524, 2527,
    2540, 2624, 2641, 2658, 2700, 2701, 2704, 2718, 2729, 2739, 2746, 2747, 2758, 2778, 2788, 2790, 2794, 2797, 2798,
    2810, 2836, 2841, 2843, 2850, 2858, 2862, 2864, 2874, 2881, 2885, 2889, 2894, 2896, 2904, 2909, 2914, 2934, 2942,
    2943, 2947, 2948, 2952, 2955, 2957, 2963, 2968, 2972, 2973, 2974, 2989, 3002, 3022, 3027, 3036, 3037, 3053, 3072,
    3076, 3096, 3117, 3129, 3141, 3143, 3146, 3159, 3164, 3170, 3195, 3202, 3213, 3231, 3233, 3245, 3253, 3258, 3286,
    3297, 3299, 3301, 3305, 3312, 3314, 3323, 3328, 3330, 3331, 3334, 3335, 3337, 3352, 3355, 3369, 3371, 3376, 3377,
    3385, 3388, 3400, 3414, 3423, 3443, 3448, 3463, 3468, 3472, 3476, 3484, 3486, 3488, 3499, 3505, 3514, 3519, 3528,
    3531, 3532, 3533, 3558, 3565, 3568, 3570, 3576, 3579, 3580, 3585, 3587, 3598, 3601, 3608, 3616, 3621, 3623, 3631,
    3646, 3655, 3665, 3671, 3699, 3706, 3715, 3722, 3724, 3725, 3727, 3731, 3741, 3745, 3759, 3761, 3774, 3785, 3795,
    3803, 3810, 3813, 3815, 3832, 3833, 3847, 3850, 3853, 3859, 3860, 3866, 3870, 3877, 3879, 3881, 3891, 3896, 3908,
    3913, 3915, 3917, 3921, 3922, 3926, 3931, 3933, 3938, 3943, 3947, 3949, 3950, 3960, 3964, 3971, 3973, 3977, 3979,
    3981, 3984, 3986, 3999, 4013, 4016, 4021, 4022, 4029, 4035, 4046, 4049, 4058, 4083, 4085, 4100, 4112, 4113, 4127,
    4131, 4134, 4139, 4143, 4182, 4188, 4191, 4193, 4207, 4213, 4215, 4217, 4228, 4238, 4239, 4240, 4241, 4243, 4253,
    4255, 4260, 4267, 4279, 4284, 4287, 4309, 4320, 4330, 4335, 4336, 4340, 4349, 4355, 4358, 4371, 4380, 4387, 4400,
    4406, 4422, 4429, 4431, 4433, 4438, 4441, 4445, 4449, 4453, 4456, 4458, 4470, 4471, 4473, 4476, 4479, 4499, 4502,
    4511, 4513, 4537, 4542, 4543, 4556, 4572, 4590, 4596, 4602, 4603, 4608, 4615, 4618, 4619, 4622, 4631, 4636, 4651,
    4669, 4678, 4683, 4693, 4698, 4713, 4717, 4728, 4738, 4744, 4756, 4771, 4777, 4785, 4788, 4790, 4794, 4799, 4819,
    4834, 4836, 4838, 4840, 4847, 4861, 4872, 4881, 4914, 4945, 4965, 4983, 4987, 5010, 5015, 5017, 5058, 5066, 5076,
    5101, 5116, 5121, 5124, 5145, 5151, 5158, 5160, 5172, 5175, 5178, 5188, 5191, 5194, 5210, 5217, 5219, 5229, 5238,
    5243, 5249, 5251, 5257, 5272, 5282, 5284, 5305, 5309, 5316, 5321, 5342, 5366, 5369
];
// Utility function to distribute logos across multiple columns
// This ensures each column has a balanced number of logos
const distributeLogos = (allLogos: Logo[], columnCount: number): Logo[][] => {
    const shuffled = shuffleArray(allLogos);
    const columns: Logo[][] = Array.from({ length: columnCount }, () => []);

    // Distribute logos evenly across columns
    shuffled.forEach((logo, index) => {
        columns[index % columnCount].push(logo);
    });

    // Ensure all columns have the same number of logos by filling shorter columns
    const maxLength = Math.max(...columns.map((col) => col.length));
    columns.forEach((col) => {
        while (col.length < maxLength) {
            col.push(shuffled[Math.floor(Math.random() * shuffled.length)]);
        }
    });

    return columns;
};

// Props for the LogoColumn component
interface LogoColumnProps {
    logos: Logo[];
    index: number;
    currentTime: number;
}

// LogoColumn component: Displays a single column of animated logos
// eslint-disable-next-line react/display-name
const LogoColumn: React.FC<React.PropsWithChildren<React.PropsWithChildren<LogoColumnProps>>> = React.memo(
    ({ logos, index, currentTime }) => {
        const cycleInterval = 2000; // Time each logo is displayed (in milliseconds)
        const columnDelay = index * 200; // Stagger the start of each column's animation
        // Calculate which logo should be displayed based on the current time
        const adjustedTime = (currentTime + columnDelay) % (cycleInterval * logos.length);
        const currentIndex = Math.floor(adjustedTime / cycleInterval);

        // Memoize the current logo to prevent unnecessary re-renders
        const CurrentLogo = useMemo(() => logos[currentIndex].img, [logos, currentIndex]);

        return (
            // Framer Motion component for the column container
            <motion.div
                className="tw-relative tw-h-32 tw-w-40 tw-overflow-hidden"
                initial={{ opacity: 0, y: 50 }} // Start invisible and below final position
                animate={{ opacity: 1, y: 0 }} // Animate to full opacity and final position
                transition={{
                    delay: index * 0.1, // Stagger the animation of each column
                    duration: 0.5,
                    ease: 'easeOut'
                }}
            >
                {/* AnimatePresence enables animation of components that are removed from the DOM */}
                {/* @ts-ignore */}
                <AnimatePresence mode="wait">
                    {/* Framer Motion component for each logo */}
                    <motion.div
                        key={`${logos[currentIndex].id}-${currentIndex}`}
                        className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center"
                        // Animation for when the logo enters
                        initial={{ y: '10%', opacity: 0, filter: 'blur(8px)' }}
                        // Animation for when the logo is displayed
                        animate={{
                            y: '0%',
                            opacity: 1,
                            filter: 'blur(0px)',
                            transition: {
                                type: 'spring',
                                stiffness: 300,
                                damping: 20,
                                mass: 1,
                                bounce: 0.2,
                                duration: 0.5
                            }
                        }}
                        // Animation for when the logo exits
                        exit={{
                            y: '-20%',
                            opacity: 0,
                            filter: 'blur(6px)',
                            transition: {
                                type: 'tween',
                                ease: 'easeIn',
                                duration: 0.3
                            }
                        }}
                    >
                        <CurrentLogo className="tw-h-20 tw-max-h-[80%] tw-w-20 tw-max-w-[80%] tw-object-contain md:tw-h-32 md:tw-w-32" />
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        );
    }
);

// Main LogoCarousel component
function LogoCarousel() {
    const [logoSets, setLogoSets] = useState<Logo[][]>([]);
    const [currentTime, setCurrentTime] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    const loopItems = useMemo<Logo[]>(() => {
        return Array.from({ length: 30 }, (_, index) => {
            // eslint-disable-next-line
            const logoId = logoIds[Math.floor(Math.random() * logoIds.length)];
            const DynamicLogo: React.FC<React.PropsWithChildren<React.PropsWithChildren<{ className?: string }>>> = ({
                className
            }) => {
                const baseClasses =
                    'tw-relative tw-h-[120px] tw-min-h-[120px] tw-w-[120px] tw-min-w-[120px] tw-overflow-hidden tw-rounded-full tw-transition-all tw-duration-200 hover:tw-scale-105 ';
                const combinedClass = className ? `${baseClasses} ${className}` : baseClasses;
                return (
                    <div className={combinedClass}>
                        <Link href={`/club/`}>
                            <Image
                                loading="eager"
                                src={imageSource(logoId)}
                                alt="Logo"
                                objectFit="cover"
                                width={128}
                                height={120}
                            />
                        </Link>
                    </div>
                );
            };

            return {
                name: `Logo-${logoId}`,
                id: logoId * 1000 + index,
                img: DynamicLogo
            };
        });
    }, []);

    useEffect(() => {
        const distributedLogos = distributeLogos(loopItems, 6);
        setLogoSets(distributedLogos);
    }, [loopItems]);

    const updateTime = useCallback(() => {
        setCurrentTime((prevTime) => prevTime + 100);
    }, []);

    useEffect(() => {
        if (isHovered) return;
        const intervalId = setInterval(updateTime, 100);
        return () => clearInterval(intervalId);
    }, [updateTime, isHovered]);

    return (
        <div
            className="tw-flex tw-w-full tw-justify-around tw-gap-4 md:tw-gap-8"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {logoSets.map((logos, index) => {
                let visibilityClass = 'tw-block';

                if (index >= 2 && index < 4) {
                    visibilityClass = 'tw-hidden md:tw-block';
                } else if (index >= 4) {
                    visibilityClass = 'tw-hidden xl:tw-block';
                }

                return (
                    <div key={index} className={visibilityClass}>
                        <LogoColumn logos={logos} index={index} currentTime={currentTime} />
                    </div>
                );
            })}
        </div>
    );
}

export { LogoCarousel };
